// ============================================================================
// Cobro en la caja de contingencia (paso 3c · oct-2026).
//
// Solo efectivo y datáfono manual (con el número de aprobación del voucher):
// Bold, QR y pagos en línea necesitan internet. Un solo pago por pedido; si
// la mesa divide la cuenta, se cobra en el datáfono o en efectivo por partes
// y se anota el total aquí. La propina se calcula como en caja (hacia abajo
// al múltiplo de 100).
// ============================================================================
import { useMemo, useState } from 'react';
import { Banknote, CreditCard, X } from 'lucide-react';
import { formatMoney, limpiarMonto, tecladoMonto } from '@/lib/moneda';
import * as caja from '../../../lib/caja-contingencia';
import { nombreDestino, type PedidoContingencia, type PagoLocal, type MetodoContingencia } from '../../../lib/caja-contingencia';
import type { TraductorCaja } from './caja-contingencia';

const PORCENTAJES = [0, 10] as const;

export function CobroContingencia({ t, pedido, onCancelar, onConfirmar }: {
  t: TraductorCaja;
  pedido: PedidoContingencia;
  onCancelar: () => void;
  onConfirmar: (datos: { propina: number; pagos: PagoLocal[]; correoFE?: string }) => void | Promise<void>;
}) {
  const facturado = !!pedido.servidor?.facturado;
  const consumo = caja.consumo(pedido);
  const [pct, setPct] = useState<number>(facturado ? 0 : 10);
  const [metodo, setMetodo] = useState<MetodoContingencia>('efectivo');
  const [recibido, setRecibido] = useState('');
  const [voucher, setVoucher] = useState('');
  const [correo, setCorreo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const propina = facturado ? 0 : caja.propinaPorcentaje(consumo, pct);
  const total = caja.totalACobrar(pedido, propina);
  const recibidoNum = recibido.trim() ? Number(limpiarMonto(recibido)) : null;
  const pago: PagoLocal = useMemo(() => (
    metodo === 'efectivo'
      ? { metodo, monto: total, ...(recibidoNum != null ? { recibido: recibidoNum } : {}) }
      : { metodo, monto: total, voucher: voucher.trim() }
  ), [metodo, total, recibidoNum, voucher]);
  const cambio = caja.cambioEfectivo([pago]);
  const datos = { propina, pagos: [pago], ...(correo.trim() ? { correoFE: correo.trim() } : {}) };
  const problema = caja.problemaDeCobro(pedido, datos);

  const confirmar = async () => {
    if (problema || enviando) return;
    setEnviando(true);
    try { await onConfirmar(datos); } finally { setEnviando(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[var(--ink)]/50 p-4">
      <div role="dialog" aria-modal="true" data-testid="caja-cobro" className="w-full max-w-md rounded-2xl bg-[var(--surface)] p-5 shadow-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="m-0 text-[18px] font-extrabold">{t('cobroTitulo', { destino: nombreDestino(pedido) })}</h2>
          <button type="button" onClick={onCancelar} aria-label={t('cancelar')} className="press cursor-pointer rounded-lg border-0 bg-transparent p-1">
            <X className="size-5" />
          </button>
        </div>

        {!facturado && (
          <div className="mb-3">
            <div className="mb-1.5 text-[12px] font-extrabold uppercase tracking-wide text-[var(--muted-ink)]">{t('propina')}</div>
            <div className="flex gap-2">
              {PORCENTAJES.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPct(p)}
                  className={`press flex-1 cursor-pointer rounded-xl border px-3 py-2 text-[13.5px] font-bold ${
                    pct === p ? 'border-[var(--ink)] bg-[var(--ink)] text-white' : 'border-[var(--line-2)] bg-[var(--surface-2)]'
                  }`}
                >
                  {p === 0 ? t('sinPropina') : `${p}% · ${formatMoney(caja.propinaPorcentaje(consumo, p))}`}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mb-3 grid grid-cols-2 gap-2">
          {(['efectivo', 'datafono'] as const).map(m => (
            <button
              key={m}
              type="button"
              onClick={() => setMetodo(m)}
              data-testid={`caja-metodo-${m}`}
              className={`press inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-[14px] font-bold ${
                metodo === m ? 'border-[var(--ink)] bg-[var(--ink)] text-white' : 'border-[var(--line-2)] bg-[var(--surface-2)]'
              }`}
            >
              {m === 'efectivo' ? <Banknote className="size-4" /> : <CreditCard className="size-4" />}
              {m === 'efectivo' ? t('efectivo') : t('datafono')}
            </button>
          ))}
        </div>
        <p className="m-0 mb-3 text-[11.5px] text-[var(--muted-ink)]">{t('boldDesactivado')}</p>

        {metodo === 'efectivo' ? (
          <label className="mb-3 block">
            <span className="mb-1 block text-[12.5px] font-bold">{t('recibido')}</span>
            <input
              value={recibido}
              onChange={e => setRecibido(e.target.value)}
              inputMode={tecladoMonto()}
              data-testid="caja-recibido"
              className="w-full rounded-xl border border-[var(--line-2)] px-3 py-2 text-[15px]"
            />
            {recibidoNum != null && (
              <span className="mt-1 block text-[13px] font-bold text-[var(--ok-ink)]">{t('cambio')}: {formatMoney(cambio)}</span>
            )}
          </label>
        ) : (
          <label className="mb-3 block">
            <span className="mb-1 block text-[12.5px] font-bold">{t('voucher')}</span>
            <input
              value={voucher}
              onChange={e => setVoucher(e.target.value)}
              data-testid="caja-voucher"
              className="w-full rounded-xl border border-[var(--line-2)] px-3 py-2 text-[15px]"
            />
            <span className="mt-1 block text-[11.5px] text-[var(--muted-ink)]">{t('datafonoNota')}</span>
          </label>
        )}

        <label className="mb-4 block">
          <span className="mb-1 block text-[12.5px] font-bold">{t('correoFE')}</span>
          <input
            type="email"
            value={correo}
            onChange={e => setCorreo(e.target.value)}
            data-testid="caja-correo"
            className="w-full rounded-xl border border-[var(--line-2)] px-3 py-2 text-[14px]"
          />
          <span className="mt-1 block text-[11.5px] text-[var(--muted-ink)]">{t('correoFENota')}</span>
        </label>

        <div className="mb-3 flex items-baseline justify-between">
          <span className="text-[14px] font-bold text-[var(--muted-ink)]">{t('total')}</span>
          <span data-testid="caja-cobro-total" className="text-[22px] font-extrabold tabular-nums">{formatMoney(total)}</span>
        </div>
        {problema && (recibido || voucher || correo) && (
          <p role="alert" className="m-0 mb-3 text-[12.5px] font-bold text-[var(--danger-ink)]">{problema}</p>
        )}
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={onCancelar} className="press cursor-pointer rounded-xl border border-[var(--line-2)] bg-[var(--surface-2)] px-3 py-2.5 text-[14px] font-bold">
            {t('cancelar')}
          </button>
          <button
            type="button"
            onClick={confirmar}
            disabled={!!problema || enviando}
            data-testid="caja-confirmar-cobro"
            className="press cursor-pointer rounded-xl border-0 bg-[var(--ok)] px-3 py-2.5 text-[14px] font-extrabold text-white disabled:opacity-50"
          >
            {enviando ? t('guardando') : t('confirmarCobro')}
          </button>
        </div>
      </div>
    </div>
  );
}
